import React, { useState } from 'react';
import { SongSimple } from '@round/api';
import useUrlState from '../../../../Hooks/useUrlState';
import { PaginatedRequest } from '../../../../App.types';
import SongAdminTable from './SongAdminTable/SongAdminTable';
import SearchInput from '../../../../ui/DataEntry/SearchInput/SearchInput';
import styles from './SongAdmin.module.css';
import UpdateSongModal from '../UpdateSongModal/UpdateSongModal';
import CreateSongModal from '../CreateSongModal/CreateSongModal';
import Button from '../../../../ui/Buttons/Button/Button';
import { showNotification } from '../../../../helpers';
import { useSongs } from './useSongs';

type UrlState = PaginatedRequest & { search: string };

const initialUrlState: UrlState = { page: 1, page_size: 25, search: '' };

const SongAdmin = () => {
    const [isAddSongModalOpen, setIsAddSongModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [songToUpdate, setSongToUpdate] = useState<SongSimple | null>(null);

    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);
    const page = !isNaN(Number(urlState.page)) ? Number(urlState.page) : initialUrlState.page;
    const pageSize = !isNaN(Number(urlState.page_size)) ? Number(urlState.page_size) : initialUrlState.page_size;

    const { songs, updateSong, count, hasErrorLoading, isLoading, refresh, isInitialized } = useSongs({
        page: page,
        page_size: pageSize,
        search: urlState.search,
    });

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Songs</h1>

            <div className={styles.header}>
                <SearchInput
                    className={styles.searchInput}
                    value={urlState.search}
                    onChange={(search) => setUrlState({ search, page: 1 })}
                />

                <Button type="filled" color="black" onClick={() => setIsAddSongModalOpen(true)}>
                    Add Song
                </Button>
            </div>

            <SongAdminTable
                loading={isLoading || (!isInitialized && !hasErrorLoading)}
                data={songs}
                count={count}
                page={page}
                setPage={(page) => setUrlState({ page })}
                pageSize={pageSize}
                setPageSize={(pageSize) => setUrlState({ page_size: pageSize ?? 10 })}
                noDataLabel={hasErrorLoading ? 'Sorry, we are experiencing technical issues' : 'No data available'}
                onRowClick={(row) => {
                    setSongToUpdate(row);
                    setIsUpdateModalOpen(true);
                }}
            />

            <UpdateSongModal
                isOpen={isUpdateModalOpen}
                song={songToUpdate}
                onClose={() => {
                    setIsUpdateModalOpen(false);
                    setSongToUpdate(null);
                }}
                onUpdate={(song) => {
                    updateSong(song);
                    setIsUpdateModalOpen(false);
                    setSongToUpdate(null);
                }}
                onRefreshed={(song) => {
                    updateSong(song);
                    setSongToUpdate(song);
                }}
            />

            <CreateSongModal
                isModalOpen={isAddSongModalOpen}
                closeModal={() => setIsAddSongModalOpen(false)}
                onCreated={() => {
                    setIsAddSongModalOpen(false);
                    showNotification('Song created', 'info');
                    refresh();
                }}
            />
        </div>
    );
};

export default SongAdmin;
